import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UseDatepickerProps } from "@datepicker-react/hooks";
import { withErrorBoundary } from "@sentry/nextjs";
import { format } from "date-fns";
import { usePathname } from "next/navigation";
import useSWR from "swr";
import { NewDayObject, UpdateAvailibitlityOfDates } from "commonModule/calendar";
import { OriginalCalendarObject } from "commonModule/services/common";
import CloseIcon from "components/icons-svg/CloseIcon";
import CVAModal from "components/modals/common/CVAModal";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import Datepicker from "../datepicker";
import CalendarPopOver from "./CalendarPopOver";
interface DateRangeInputDetailProps extends UseDatepickerProps {
  calendarRates: OriginalCalendarObject[];
  checkArrivalDeparture?: boolean;
  isDisabled?: boolean;
  isOpen: boolean;
  modalFooter?: ReactNode;
  modalHeader?: ReactNode;
  onClose: () => void;
  onOpen: () => void;
  onReset?: () => void;
  vertical?: boolean;
  usedFromContactHost?: boolean;
  shouldMaximizeProfitBasedOnPmsSystem?: boolean;
  isDisplayClearDates?: boolean;
}
const normalizeDatesArray = (arr: NewDayObject[]) => {
  const resObj = {};
  arr.forEach(dayObj => {
    if (dayObj.valid_from) resObj[dayObj.valid_from] = dayObj;
  });
  return resObj;
};
const DateRangeInputDetail = (props: DateRangeInputDetailProps) => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    isOpen,
    onOpen,
    onClose,
    startDate,
    endDate,
    minBookingDate,
    maxBookingDate,
    focusedInput,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    onDatesChange,
    initialVisibleMonth,
    vertical = false,
    onReset = () => {},
    isDisabled = false,
    calendarRates = [],
    checkArrivalDeparture,
    modalHeader,
    modalFooter,
    usedFromContactHost,
    shouldMaximizeProfitBasedOnPmsSystem = true,
    isDisplayClearDates = false
  } = props;
  const [isLargerThan992] = useMediaQuery("(min-width: 992px)");
  const pathname = usePathname();
  const isPaymentPage = pathname?.startsWith("/payment");
  const isFavoritesPage = pathname?.startsWith("/favorites");
  const [annotatedCalendarDates, setAnnotatedCalendarDates] = useState<Record<string, NewDayObject>>({});
  const calendarWorker = useRef<Worker | null>(null);
  useEffect(() => {
    if (typeof Worker === "undefined") return;

    // Register the worker.
    calendarWorker.current = new Worker(new URL("./worker/calendar", import.meta.url));

    // Receive computation results from the worker.
    calendarWorker.current.onmessage = function (e) {
      const data = (e.data as {
        annotatedDates: NewDayObject[];
        event: string;
      }) || {};
      if (data.event !== "calendarAnnotation") return;
      setAnnotatedCalendarDates(normalizeDatesArray(data?.annotatedDates || []));
    };
    return () => {
      // Terminate the worker when unmounting.
      calendarWorker.current?.terminate();
    };
  }, []);
  const annotator = useCallback(({
    calendarRates: calendarRatesLocal,
    checkArrivalDeparture: checkArrivalDepartureLocal,
    shouldMaximizeProfit,
    startDate: startDateLocal
  }: {
    key: string;
    calendarRates: OriginalCalendarObject[];
    checkArrivalDeparture: boolean | undefined;
    shouldMaximizeProfit: boolean;
    startDate: string | null;
  }) => {
    if (calendarWorker.current) {
      // Use worker if it's available and supported.
      calendarWorker.current.postMessage({
        calendarRates: calendarRatesLocal,
        checkArrivalDeparture: checkArrivalDepartureLocal,
        shouldMaximizeProfit,
        startDate: startDateLocal,
        event: "calendarAnnotation"
      });
    } else {
      // Web workers are not supported in this environment.
      setAnnotatedCalendarDates(normalizeDatesArray(UpdateAvailibitlityOfDates(calendarRatesLocal, checkArrivalDepartureLocal || false, shouldMaximizeProfit, startDateLocal)));
    }
  }, []);

  // here we will create Updated Calendar array which will say what dates are disabled/striked/enabled
  // Used SWR for deduping the same requests.
  useSWR(calendarRates ? {
    key: "/annotateCalendar",
    calendarRates,
    checkArrivalDeparture,
    focusedInput,
    shouldMaximizeProfit: shouldMaximizeProfitBasedOnPmsSystem,
    startDate: focusedInput === "endDate" && startDate ? format(startDate, "yyyy-MM-dd") : null
  } : null, annotator, {
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnFocus: false,
    dedupingInterval: 2000,
    errorRetryCount: 3
  });
  const calendar = useMemo(() => {
    return <Datepicker isAnnotated={true} onReset={onReset} onClose={onClose} startDate={startDate} endDate={endDate} minBookingDate={minBookingDate} maxBookingDate={maxBookingDate} focusedInput={focusedInput} onDatesChange={onDatesChange} vertical={vertical} initialVisibleMonth={initialVisibleMonth} annotatedCalendarDates={annotatedCalendarDates}
    // show all months [ which are included in booking range ] in mobile view calendar
    activeMonths={undefined} isDisplayClearDates={isDisplayClearDates} />;
  }, [annotatedCalendarDates, endDate, focusedInput, initialVisibleMonth, isDisplayClearDates, maxBookingDate, minBookingDate, onClose, onDatesChange, onReset, startDate, vertical]);
  return <>
      {/* >= 992px AND Favorites page */}
      {isLargerThan992 && isFavoritesPage && <CVAModal isOpen={isOpen} onClose={onClose} intent={"modal"} size={"large"} isCloseIcon={false} className='max-w-[700px] !rounded-[16px]' modalOverlayColor='bg-[#1c1d33cc]'>
          <div className='relative flex w-full items-center justify-between px-4 pb-6 pt-8'>
            <header className='w-full bg-white'>{modalHeader}</header>
            <button aria-label='close calendar modal' className='btn btn-ghost absolute right-4 top-5 h-6 w-6 min-w-[0] rounded-lg p-0 text-textColor hover:bg-gray-200/50' onClick={onClose}>
              <CloseIcon className='h-[22px] w-[22px] text-[#8D91A5]' />
            </button>
          </div>

          <main className={`min-h-full w-full overflow-y-auto overflow-x-hidden px-10 pb-4`}>
            {calendar}
          </main>
          <footer className='rounded-b-2xl bg-white px-4 pb-6 text-center'>
            {modalFooter}
          </footer>
        </CVAModal>}

      {/* for above 992px screen size */}
      {!isFavoritesPage && isLargerThan992 && <CalendarPopOver onOpen={onOpen} onClose={onClose} isDisabled={isDisabled} usedFromContactHost={usedFromContactHost}>
          {calendar}
        </CalendarPopOver>}
      {!isLargerThan992 && <CVAModal isOpen={isOpen} onClose={onClose} intent={"modal"} size={"fullScreen"} className='min-h-[100dvh]' closeOnClickOutSide={false}>
          {/* for below 456px screen size */}
          <header className='fixed top-0 z-[1] w-full bg-white px-8 pt-8'>
            {modalHeader}
            {/* <Divider className='mt-3' /> */}
          </header>
          <main className={`min-h-full w-full overflow-y-auto overflow-x-hidden pb-[81px] ${isFavoritesPage ? "" : "px-4"} ${isPaymentPage ? "pt-[78px]" : "pt-[80px]"}`}>
            {calendar}
          </main>
          <footer className='fixed bottom-0 block w-full bg-white p-0'>
            {modalFooter}
          </footer>
        </CVAModal>}
    </>;
};
export default withErrorBoundary(DateRangeInputDetail, {
  beforeCapture: scope => {
    scope.setTag("ws_location", "DateRangeInputDetail");
  }
});